import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { InventorySectionNav } from '../components/inventory/InventorySectionNav';
import {
  createParseJob,
  downloadParseHtmlReport,
  downloadParseMatchedCsv,
  getParseJob,
  getParseReport,
  importParseToInventory,
  listParseJobs,
  looksLikeColumnarCardList,
  ParseImportResponse,
  ParseJobListItem,
  ParseMatchedItem,
  ParseJobStatusResponse,
  ParseReportResponse,
} from '../api/parse';
import { CardImage } from '../components/card/CardImage';
import { IntakePricingPanel } from '../components/pricing/IntakePricingPanel';
import { ManapoolSalesChart } from '../components/pricing/ManapoolSalesChart';
import { PolicyPriceDebug } from '../components/pricing/PolicySignalsPanel';
import { validateFile, validatePastedText } from '../services/uploads';
import { borderRadius, colors, shadows, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatRelativeTime } from '../utils/datetime';

const POLL_INTERVAL_MS = 2000;
const PARSE_HISTORY_HOURS = 72;

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function statusColor(status: string): string {
  if (status === 'completed') return colors.success;
  if (status === 'failed') return colors.error;
  if (status === 'processing' || status === 'queued') return colors.info;
  return colors.textSecondary;
}

interface ParsedListItem {
  key: string;
  raw: ParseMatchedItem;
  displayName: string;
  setName: string;
  cardNumber: string;
  condition: string;
  finish: string;
  maxQuantity: number;
  selectedQuantity: number;
  selected: boolean;
  selectedUnitPrice: number;
  tcgplayerMarket: number | null;
  manapoolMarket: number | null;
  ckBuylist: number | null;
  tcgExportMarket: number | null;
  tcgLow: number | null;
  manapoolLow: number | null;
  calculatedOffer: number | null;
  offerCkFloored: boolean;
  priceSource: string | null;
  policyPrice: number | null;
  policyDebug: PolicyPriceDebug | null;
  manapoolRecentSales: ParseMatchedItem['manapool_recent_sales'];
  onHandQty: number;
  overstock: boolean;
  stockOverride: boolean;
  imageUrl: string | null;
  scryfallId: string | null;
  reverseScryfallId: string | null;
  reverseImageUrl: string | null;
  isDualFaced: boolean;
  warnings: string[];
}

export const InventoryParsePage: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingHtml, setIsDownloadingHtml] = useState(false);
  const [job, setJob] = useState<ParseJobStatusResponse | null>(null);
  const [report, setReport] = useState<ParseReportResponse | null>(null);
  const [importResult, setImportResult] = useState<ParseImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [lineItems, setLineItems] = useState<ParsedListItem[]>([]);
  const [recentJobs, setRecentJobs] = useState<ParseJobListItem[]>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [isLoadingHistoric, setIsLoadingHistoric] = useState(false);

  const refreshRecentJobs = useCallback(async () => {
    setIsLoadingRecent(true);
    try {
      const jobs = await listParseJobs({ hours: PARSE_HISTORY_HOURS, limit: 50 });
      setRecentJobs(jobs);
    } catch (historyError) {
      console.error('Failed to load recent parse jobs:', historyError);
    } finally {
      setIsLoadingRecent(false);
    }
  }, []);

  useEffect(() => {
    void refreshRecentJobs();
  }, [refreshRecentJobs]);

  const canPoll = useMemo(() => {
    if (!job) return false;
    return job.status === 'queued' || job.status === 'processing';
  }, [job]);

  useEffect(() => {
    if (!canPoll || !job?.jobId) {
      return;
    }

    let cancelled = false;

    const runPoll = async () => {
      try {
        const latest = await getParseJob(job.jobId);
        if (!cancelled) {
          setJob(latest);
          if (latest.status === 'completed' || latest.status === 'failed') {
            void refreshRecentJobs();
          }
        }
      } catch (pollError) {
        if (!cancelled) {
          setError(pollError instanceof Error ? pollError.message : 'Failed to check parse job status');
        }
      }
    };

    void runPoll();
    const intervalId = window.setInterval(() => {
      void runPoll();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [canPoll, job?.jobId, refreshRecentJobs]);

  useEffect(() => {
    if (!job?.jobId || job.status !== 'completed' || report) {
      return;
    }

    let cancelled = false;

    const loadReport = async () => {
      try {
        const nextReport = await getParseReport(job.jobId);
        if (!cancelled) {
          setReport(nextReport);
        }
      } catch (reportError) {
        if (!cancelled) {
          setError(reportError instanceof Error ? reportError.message : 'Failed to load parse report');
        }
      }
    };

    void loadReport();

    return () => {
      cancelled = true;
    };
  }, [job, report]);

  useEffect(() => {
    if (!report?.matched_items) {
      setLineItems([]);
      return;
    }

    setLineItems(
      report.matched_items.map((item, idx) => {
        const maxQuantity = Math.max(1, Number(item.quantity ?? 1));
        const tcg = item.tcgplayer_market ?? null;
        const mana = item.manapool_market ?? null;
        const ck = item.ck_buylist ?? null;
        const selectedUnitPrice = Number(item.price ?? ((tcg ?? 0) > 0 ? (tcg ?? 0) : (mana ?? 0) > 0 ? (mana ?? 0) : 0));
        const defaultOffer = item.calculated_offer ?? null;
        const overstock = Boolean(item.overstock);
        const onHandQty = Number(item.on_hand_qty ?? 0);

        return {
          key: `${item.card_name || item.name || item.product_name || 'card'}-${item.set || item.set_name || 'set'}-${item.card_number || idx}`,
          raw: item,
          displayName: item.card_name || item.name || item.product_name || 'Unknown card',
          setName: item.set || item.set_name || 'Unknown set',
          cardNumber: item.card_number || '',
          condition: item.condition || 'NM',
          finish: item.finish || 'Normal',
          maxQuantity,
          selectedQuantity: maxQuantity,
          selected: !overstock,
          selectedUnitPrice,
          tcgplayerMarket: tcg,
          manapoolMarket: mana,
          ckBuylist: ck,
          tcgExportMarket: item.tcg_export_market ?? null,
          tcgLow: item.tcg_low ?? null,
          manapoolLow: item.manapool_low ?? null,
          calculatedOffer: defaultOffer,
          offerCkFloored: Boolean(item.offer_ck_floored),
          priceSource: item.price_source || null,
          policyPrice: item.policy_price ?? item.policy_price_debug?.policy_price ?? null,
          policyDebug: item.policy_price_debug ?? null,
          manapoolRecentSales: item.manapool_recent_sales ?? null,
          onHandQty,
          overstock,
          stockOverride: false,
          imageUrl: item.image_url || null,
          scryfallId: item.scryfall_id || null,
          reverseScryfallId: item.reverse_scryfall_id || null,
          reverseImageUrl: item.reverse_image_url || null,
          isDualFaced: Boolean(item.is_dual_faced),
          warnings: item.warnings || [],
        };
      }),
    );
  }, [report]);

  const handleAcceptedFile = (file: File | null) => {
    setError(null);
    setSuccess(null);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const validation = validateFile(file);
    if (!validation.valid) {
      setSelectedFile(null);
      setError(validation.error || 'Invalid file type. Please upload a .txt, .csv, or .tsv file.');
      return;
    }

    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.txt') && !lowerName.endsWith('.csv') && !lowerName.endsWith('.tsv')) {
      setSelectedFile(null);
      setError('Invalid file type. Please upload a .txt, .csv, or .tsv file.');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleAcceptedFile(event.target.files?.[0] ?? null);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    handleAcceptedFile(event.dataTransfer.files?.[0] ?? null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPastedText(event.target.value);
    setError(null);
    setSuccess(null);
  };

  const canStartParse = Boolean(selectedFile || pastedText.trim().length > 0);

  const updateLineItem = (key: string, updater: (item: ParsedListItem) => ParsedListItem) => {
    setLineItems((prev) => prev.map((item) => (item.key === key ? updater(item) : item)));
  };

  const handleToggleItemSelection = (key: string, selected: boolean) => {
    updateLineItem(key, (item) => {
      if (selected && item.overstock && !item.stockOverride) {
        return item;
      }
      return { ...item, selected };
    });
  };

  const handleStockOverride = (key: string, stockOverride: boolean) => {
    updateLineItem(key, (item) => ({
      ...item,
      stockOverride,
      selected: stockOverride ? true : false,
    }));
  };

  const handleQuantityChange = (key: string, value: number) => {
    const quantity = Number.isFinite(value) ? value : 1;
    updateLineItem(key, (item) => ({
      ...item,
      selectedQuantity: Math.max(1, Math.min(item.maxQuantity, Math.floor(quantity))),
    }));
  };

  const handleSelectAll = () => {
    setLineItems((prev) =>
      prev.map((item) => ({
        ...item,
        selected: !item.overstock || item.stockOverride,
      })),
    );
  };

  const handleDeselectAll = () => {
    setLineItems((prev) => prev.map((item) => ({ ...item, selected: false })));
  };

  const handleDeselectBulk = () => {
    setLineItems((prev) =>
      prev.map((item) => ({
        ...item,
        selected: item.selectedUnitPrice > 2.99 && (!item.overstock || item.stockOverride),
      })),
    );
  };

  const handlePrintSelected = () => {
    window.print();
  };

  const handleStartParse = async () => {
    let uploadFile: File | null = selectedFile;

    if (!uploadFile && pastedText.trim().length > 0) {
      const textValidation = validatePastedText(pastedText);
      if (!textValidation.valid) {
        setError(textValidation.error || 'Pasted text is too large or invalid.');
        return;
      }
      // Columnar paste (TSV/CSV headers) must use .csv so the backend CSV path runs.
      // Plain marketplace lines stay .txt for the freeform text parser.
      const columnar = looksLikeColumnarCardList(pastedText);
      uploadFile = new File(
        [pastedText],
        `pasted-list-${Date.now()}.${columnar ? 'csv' : 'txt'}`,
        { type: columnar ? 'text/csv' : 'text/plain' },
      );
    }

    if (!uploadFile) {
      setError('Choose a .txt/.csv/.tsv file or paste list text first.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    setReport(null);
    setImportResult(null);

    try {
      const queued = await createParseJob(uploadFile);
      setJob(queued);
      setSuccess(`Parse job ${queued.jobId} queued.`);
      void refreshRecentJobs();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to start parse job');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoadRecentJob = async (jobId: string) => {
    if (isLoadingHistoric || jobId === job?.jobId) {
      return;
    }

    setIsLoadingHistoric(true);
    setError(null);
    setSuccess(null);
    setReport(null);
    setImportResult(null);
    setLineItems([]);

    try {
      const latest = await getParseJob(jobId);
      setJob(latest);

      if (latest.status === 'completed') {
        const nextReport = await getParseReport(jobId);
        setReport(nextReport);
        setSuccess(`Loaded parse from ${latest.createdAt ? formatRelativeTime(latest.createdAt, { fallback: 'Unknown', maxDays: 3 }) : 'history'} — no re-parse needed.`);
      } else if (latest.status === 'failed') {
        setError(latest.errorMessage || 'That parse job failed.');
      } else {
        setSuccess(`Resumed parse job ${jobId} (${latest.status}).`);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load previous parse job');
    } finally {
      setIsLoadingHistoric(false);
    }
  };

  const handleImportToInventory = async () => {
    if (!job?.jobId || job.status !== 'completed') {
      return;
    }

    setIsImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await importParseToInventory(job.jobId);
      setImportResult(response);
      setSuccess(response.message || `Imported ${response.copies_created} copies to inventory.`);
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : 'Failed to import parsed results');
    } finally {
      setIsImporting(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (!job?.jobId || job.status !== 'completed') {
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      await downloadParseMatchedCsv(job.jobId);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : 'Failed to download matched CSV');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadHtml = async () => {
    if (!job?.jobId || job.status !== 'completed') {
      return;
    }

    setIsDownloadingHtml(true);
    setError(null);

    try {
      await downloadParseHtmlReport(job.jobId);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : 'Failed to download HTML report');
    } finally {
      setIsDownloadingHtml(false);
    }
  };

  const matchedCount = report?.matched_items?.length ?? 0;
  const failedCount = report?.failed_items?.length ?? 0;
  const selectedItems = lineItems.filter((item) => item.selected);
  const overstockBlockedCount = lineItems.filter((item) => item.overstock && !item.stockOverride).length;
  const overstockOverrideCount = lineItems.filter((item) => item.overstock && item.stockOverride).length;

  const selectedTotals = useMemo(() => {
    const totalValue = selectedItems.reduce((sum, item) => sum + item.selectedUnitPrice * item.selectedQuantity, 0);
    const nonBulkValue = selectedItems.reduce(
      (sum, item) => sum + (item.selectedUnitPrice > 2.99 ? item.selectedUnitPrice * item.selectedQuantity : 0),
      0,
    );
    const tcgTotal = selectedItems.reduce((sum, item) => sum + ((item.tcgplayerMarket ?? 0) * item.selectedQuantity), 0);
    const manaTotal = selectedItems.reduce((sum, item) => sum + ((item.manapoolMarket ?? 0) * item.selectedQuantity), 0);
    const ckTotal = selectedItems.reduce((sum, item) => sum + ((item.ckBuylist ?? 0) * item.selectedQuantity), 0);
    const offerTotal = selectedItems.reduce((sum, item) => sum + ((item.calculatedOffer ?? 0) * item.selectedQuantity), 0);
    const shipping = totalValue * 0.08;

    return {
      totalValue,
      nonBulkValue,
      tcgTotal,
      manaTotal,
      ckTotal,
      offerTotal,
      shipping,
      totalWithShipping: totalValue + shipping,
      selectedCount: selectedItems.length,
      selectedQuantity: selectedItems.reduce((sum, item) => sum + item.selectedQuantity, 0),
    };
  }, [selectedItems]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Parse List</h1>
        <p style={styles.subtitle}>Upload, drop, or paste a list. Review matches in batch-style cards and then import to inventory.</p>
        <InventorySectionNav />
      </div>

      <div style={styles.panel}>
        <div style={styles.sectionTitle}>1. Upload List</div>
        <p style={styles.sectionHelp}>Drag and drop a .txt/.csv/.tsv file, choose a file, or paste text.</p>

        <div
          style={{
            ...styles.dropZone,
            ...(isDragOver ? styles.dropZoneActive : {}),
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          Drop .txt, .csv, or .tsv file here
        </div>

        <div style={styles.uploadRow}>
          <input type="file" accept=".txt,.csv,.tsv,text/plain,text/csv,text/tab-separated-values" onChange={handleFileChange} />
          <button type="button" onClick={handleStartParse} disabled={!canStartParse || isSubmitting} style={styles.primaryButton}>
            {isSubmitting ? 'Starting Parse...' : 'Start Parse'}
          </button>
        </div>

        <textarea
          value={pastedText}
          onChange={handleTextChange}
          placeholder="Or paste list text here (one card per line or CSV rows)..."
          style={styles.pasteTextarea}
        />

        {selectedFile && <div style={styles.fileMeta}>Selected file: {selectedFile.name}</div>}
        {!selectedFile && pastedText.trim().length > 0 && <div style={styles.fileMeta}>Using pasted text as parse input.</div>}
      </div>

      <div style={styles.panel}>
        <div style={styles.recentHeader}>
          <div>
            <div style={styles.sectionTitle}>Recent Parses</div>
            <p style={styles.sectionHelp}>Last {PARSE_HISTORY_HOURS} hours — click to reload without re-parsing.</p>
          </div>
          <button
            type="button"
            onClick={() => void refreshRecentJobs()}
            disabled={isLoadingRecent}
            style={styles.secondaryButton}
          >
            {isLoadingRecent ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {isLoadingRecent && recentJobs.length === 0 && (
          <div style={styles.fileMeta}>Loading recent jobs...</div>
        )}

        {!isLoadingRecent && recentJobs.length === 0 && (
          <div style={styles.fileMeta}>No parse jobs in the last {PARSE_HISTORY_HOURS} hours.</div>
        )}

        {recentJobs.length > 0 && (
          <div style={styles.recentList}>
            {recentJobs.map((recent) => {
              const matched = Number(recent.summary?.matched_count ?? 0);
              const failed = Number(recent.summary?.failed_count ?? 0);
              const isActive = job?.jobId === recent.jobId;

              return (
                <button
                  key={recent.jobId}
                  type="button"
                  onClick={() => void handleLoadRecentJob(recent.jobId)}
                  disabled={isLoadingHistoric}
                  style={{
                    ...styles.recentItem,
                    ...(isActive ? styles.recentItemActive : {}),
                  }}
                >
                  <div style={styles.recentItemTop}>
                    <span style={styles.recentFileName}>
                      {recent.inputFileName || recent.jobId}
                    </span>
                    <span style={{ ...styles.recentStatus, color: statusColor(recent.status) }}>
                      {recent.status}
                    </span>
                  </div>
                  <div style={styles.recentItemMeta}>
                    <span>{formatRelativeTime(recent.createdAt, { fallback: 'Unknown', maxDays: 3 })}</span>
                    {recent.status === 'completed' && (
                      <span>
                        {matched} matched{failed > 0 ? ` · ${failed} failed` : ''}
                      </span>
                    )}
                    {recent.status === 'failed' && recent.errorMessage && (
                      <span style={{ color: colors.error }}>{recent.errorMessage}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {job && (
        <div style={styles.panel}>
          <div style={styles.sectionTitle}>2. Parse Job</div>
          <div style={styles.jobMetaRow}>
            <span style={styles.jobMetaLabel}>Job ID:</span>
            <span style={styles.jobMetaValue}>{job.jobId}</span>
          </div>
          <div style={styles.jobMetaRow}>
            <span style={styles.jobMetaLabel}>Status:</span>
            <span style={job.status === 'failed' ? styles.statusFailed : styles.statusActive}>{job.status}</span>
          </div>
          {job.errorMessage && <div style={styles.errorBox}>Parse failed: {job.errorMessage}</div>}
        </div>
      )}

      {report && (
        <div style={styles.panel}>
          <div style={styles.sectionTitle}>3. Review Results</div>

          <div style={styles.batchTotalsSticky}>
            <div style={styles.batchTotalsItem}>
              <span style={styles.batchTotalsLabel}>Selected Cards</span>
              <span style={styles.batchTotalsValue}>{selectedTotals.selectedCount} ({selectedTotals.selectedQuantity} qty)</span>
            </div>
            <div style={styles.batchTotalsItem}>
              <span style={styles.batchTotalsLabel}>TCGPlayer Total</span>
              <span style={styles.batchTotalsValue}>{formatCurrency(selectedTotals.tcgTotal)}</span>
            </div>
            <div style={styles.batchTotalsItem}>
              <span style={styles.batchTotalsLabel}>ManaPool Total</span>
              <span style={styles.batchTotalsValue}>{formatCurrency(selectedTotals.manaTotal)}</span>
            </div>
            <div style={styles.batchTotalsItem}>
              <span style={styles.batchTotalsLabel}>CK Buylist Total</span>
              <span style={styles.batchTotalsValue}>{formatCurrency(selectedTotals.ckTotal)}</span>
            </div>
            <div style={styles.batchTotalsItem}>
              <span style={styles.batchTotalsLabel}>Offer Total</span>
              <span style={styles.calculatedOfferValue}>{formatCurrency(selectedTotals.offerTotal)}</span>
            </div>
            {(overstockBlockedCount > 0 || overstockOverrideCount > 0) && (
              <div style={styles.batchTotalsItem}>
                <span style={styles.batchTotalsLabel}>Overstock</span>
                <span style={styles.batchTotalsValue}>
                  {overstockBlockedCount} blocked
                  {overstockOverrideCount > 0 ? ` · ${overstockOverrideCount} override` : ''}
                </span>
              </div>
            )}
          </div>

          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Matched Cards</div>
              <div style={styles.statValue}>{matchedCount}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Failed Cards</div>
              <div style={styles.statValue}>{failedCount}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Estimated Total</div>
              <div style={styles.statValue}>{formatCurrency(selectedTotals.totalValue)}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Offer Range</div>
              <div style={styles.statValue}>
                {formatCurrency(Number(report.summary.offer_min ?? 0))} - {formatCurrency(Number(report.summary.offer_max ?? 0))}
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Value - Bulk</div>
              <div style={styles.statValue}>{formatCurrency(selectedTotals.nonBulkValue)}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Est. Shipping (8%)</div>
              <div style={styles.statValue}>{formatCurrency(selectedTotals.shipping)}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Total + Shipping</div>
              <div style={styles.statValue}>{formatCurrency(selectedTotals.totalWithShipping)}</div>
            </div>
          </div>

          <div style={styles.tableControls}>
            <button type="button" onClick={handleSelectAll} style={styles.secondaryButton}>Select All</button>
            <button type="button" onClick={handleDeselectAll} style={styles.secondaryButton}>Deselect All</button>
            <button type="button" onClick={handleDeselectBulk} style={styles.secondaryButton}>Deselect Bulk ($2.99 or less)</button>
            <button type="button" onClick={handlePrintSelected} style={styles.secondaryButton}>Print Selected</button>
          </div>

          {lineItems.length > 0 && (
            <div style={styles.batchItemsList}>
              {lineItems.map((item) => (
                <div key={item.key} style={styles.batchItem}>
                  <div style={styles.batchItemSelection}>
                    <input
                      type="checkbox"
                      checked={item.selected}
                      disabled={item.overstock && !item.stockOverride}
                      onChange={(e) => handleToggleItemSelection(item.key, e.target.checked)}
                      title={
                        item.overstock && !item.stockOverride
                          ? 'Override overstock to select'
                          : undefined
                      }
                    />
                  </div>

                  <div style={styles.batchItemThumbnailContainer}>
                    {(item.imageUrl || item.scryfallId) ? (
                      <CardImage
                        scryfallId={item.scryfallId}
                        reverseScryfallId={item.reverseScryfallId}
                        imageUrl={item.imageUrl}
                        reverseImageUrl={item.reverseImageUrl}
                        isDualFaced={item.isDualFaced}
                        cardName={item.displayName}
                        size="fill"
                      />
                    ) : (
                      <div style={styles.batchItemPlaceholder}>No Image</div>
                    )}
                  </div>

                  <div style={styles.batchItemInfo}>
                    <div style={styles.batchItemName}>{item.displayName}</div>
                    <div style={styles.batchItemDetails}>
                      <span>{item.setName}</span>
                      {item.cardNumber && <span> #{item.cardNumber}</span>}
                      {' • '}
                      <span>{item.condition}</span>
                      {' • '}
                      <span>{item.finish}</span>
                    </div>
                    {item.overstock && (
                      <div style={styles.overstockBadge}>
                        Overstock ({item.onHandQty} on hand)
                        <label style={styles.overrideLabel}>
                          <input
                            type="checkbox"
                            checked={item.stockOverride}
                            onChange={(e) => handleStockOverride(item.key, e.target.checked)}
                          />
                          Override
                        </label>
                      </div>
                    )}
                    {item.warnings.length > 0 && <div style={styles.itemWarnings}>Warnings: {item.warnings.join('; ')}</div>}
                  </div>

                  <div style={styles.batchItemPricing}>
                    <IntakePricingPanel
                      compact
                      data={{
                        policy_price: item.policyPrice,
                        policy_price_debug: item.policyDebug,
                        calculated_offer: item.calculatedOffer,
                        offer_ck_floored: item.offerCkFloored,
                        price_source: item.priceSource,
                        tcg_export_market: item.tcgExportMarket,
                        tcg_low: item.tcgLow,
                        manapool_low: item.manapoolLow,
                        tcgplayer_market: item.tcgplayerMarket,
                        manapool_market: item.manapoolMarket,
                        ck_buylist: item.ckBuylist,
                      }}
                    />
                    <ManapoolSalesChart sales={item.manapoolRecentSales} />
                    <div style={styles.selectedUnitPrice}>
                      Selected unit {formatCurrency(item.selectedUnitPrice)}
                    </div>
                  </div>

                  <div style={styles.quantityControlWrap}>
                    <input
                      type="number"
                      min={1}
                      max={item.maxQuantity}
                      value={item.selectedQuantity}
                      onChange={(e) => handleQuantityChange(item.key, Number(e.target.value))}
                      style={styles.quantityInput}
                    />
                    <span style={styles.quantityMeta}>of {item.maxQuantity}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {report.failed_items.length > 0 && (
            <div style={styles.failedSection}>
              <h3 style={styles.failedTitle}>Failed Matches ({report.failed_items.length})</h3>
              <div style={styles.failedList}>
                {report.failed_items.map((item, idx) => (
                  <div key={`${item.card_name || item.name || idx}-${idx}`} style={styles.failedItem}>
                    <div style={styles.failedName}>{item.card_name || item.name || 'Unknown card'}</div>
                    <div style={styles.failedMeta}>
                      {item.set || 'Unknown set'}
                      {item.card_number ? ` #${item.card_number}` : ''}
                      {item.quantity ? ` • Qty: ${item.quantity}` : ''}
                    </div>
                    <div style={styles.failedReason}>{item.reason || 'No match found'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={styles.actionsRow}>
            <button type="button" onClick={handleImportToInventory} disabled={isImporting} style={styles.primaryButton}>
              {isImporting ? 'Importing...' : 'Import Matched Cards'}
            </button>
            <button type="button" onClick={handleDownloadCsv} disabled={isDownloading} style={styles.secondaryButton}>
              {isDownloading ? 'Downloading...' : 'Download Matched CSV'}
            </button>
            <button type="button" onClick={handleDownloadHtml} disabled={isDownloadingHtml} style={styles.secondaryButton}>
              {isDownloadingHtml ? 'Downloading...' : 'Download HTML Report'}
            </button>
          </div>

          {importResult && (
            <div style={styles.importSummary}>
              Imported {importResult.copies_created} copies ({importResult.total_quantity} total quantity).
            </div>
          )}
        </div>
      )}

      {error && <div style={styles.errorBox}>{error}</div>}
      {success && <div style={styles.successBox}>{success}</div>}
    </div>
  );
};

const styles = {
  container: {
    padding: spacing.xl,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.lg,
  },
  header: {
    marginBottom: spacing.sm,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: `${spacing.sm} 0 0`,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.lg,
  },
  panel: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    boxShadow: shadows.sm,
    padding: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  sectionHelp: {
    margin: `0 0 ${spacing.md}`,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  uploadRow: {
    display: 'flex',
    gap: spacing.md,
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  dropZone: {
    border: `2px dashed ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: spacing.lg,
    marginBottom: spacing.md,
    textAlign: 'center' as const,
    color: colors.textSecondary,
    backgroundColor: colors.gray50,
    fontSize: typography.small.fontSize,
  },
  dropZoneActive: {
    borderColor: colors.primary,
    color: colors.primary,
    backgroundColor: `${colors.primary}10`,
  },
  pasteTextarea: {
    width: '100%',
    marginTop: spacing.md,
    minHeight: '140px',
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    padding: spacing.md,
    fontSize: typography.small.fontSize,
    fontFamily: 'inherit',
    color: colors.text,
    resize: 'vertical' as const,
    backgroundColor: colors.white,
  },
  fileMeta: {
    marginTop: spacing.sm,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  recentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
    flexWrap: 'wrap' as const,
    marginBottom: spacing.sm,
  },
  recentList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
    maxHeight: '280px',
    overflowY: 'auto' as const,
  },
  recentItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
    textAlign: 'left' as const,
    width: '100%',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.gray50,
    padding: spacing.sm,
    cursor: 'pointer',
  },
  recentItemActive: {
    borderColor: colors.primary,
    backgroundColor: `${colors.primary}10`,
  },
  recentItemTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.sm,
    alignItems: 'center',
  },
  recentFileName: {
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  recentStatus: {
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    textTransform: 'capitalize' as const,
    flexShrink: 0,
  },
  recentItemMeta: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing.sm,
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  jobMetaRow: {
    display: 'flex',
    gap: spacing.sm,
    marginBottom: spacing.sm,
    alignItems: 'center',
  },
  jobMetaLabel: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    minWidth: '72px',
  },
  jobMetaValue: {
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontFamily: 'monospace',
  },
  statusActive: {
    color: colors.primary,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    textTransform: 'capitalize' as const,
  },
  statusFailed: {
    color: colors.error,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    textTransform: 'capitalize' as const,
  },
  batchTotalsSticky: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  batchTotalsItem: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: spacing.sm,
    backgroundColor: colors.gray50,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  batchTotalsLabel: {
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  batchTotalsValue: {
    color: colors.text,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: spacing.md,
  },
  statCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: spacing.md,
    backgroundColor: colors.gray50,
  },
  statLabel: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    marginBottom: spacing.xs,
  },
  statValue: {
    color: colors.text,
    fontSize: typographyFlat.fontSize.lg,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  tableControls: {
    display: 'flex',
    gap: spacing.sm,
    flexWrap: 'wrap' as const,
    marginTop: spacing.md,
  },
  batchItemsList: {
    marginTop: spacing.md,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  batchItem: {
    display: 'grid',
    gridTemplateColumns: '32px 80px 1.5fr 1.2fr 120px',
    gap: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.white,
    padding: spacing.md,
    alignItems: 'center',
  },
  batchItemSelection: {
    display: 'flex',
    justifyContent: 'center',
  },
  batchItemThumbnailContainer: {
    width: '72px',
    height: '100px',
    borderRadius: borderRadius.sm,
    overflow: 'hidden' as const,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.gray100,
  },
  batchItemPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  batchItemInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  batchItemName: {
    color: colors.text,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  batchItemDetails: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  itemWarnings: {
    color: colors.warning,
    fontSize: typography.caption.fontSize,
  },
  overstockBadge: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: spacing.sm,
    color: colors.error,
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  overrideLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.normal,
    cursor: 'pointer',
  },
  batchItemPricing: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  selectedUnitPrice: {
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: `${spacing.xs} ${spacing.sm}`,
    alignItems: 'center',
  },
  pricingLabel: {
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  pricingValue: {
    color: colors.text,
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  calculatedOfferValue: {
    color: colors.success,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.bold,
  },
  quantityControlWrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: spacing.xs,
  },
  quantityInput: {
    width: '80px',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: spacing.xs,
    fontSize: typography.small.fontSize,
  },
  quantityMeta: {
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  failedSection: {
    marginTop: spacing.lg,
    borderTop: `1px solid ${colors.border}`,
    paddingTop: spacing.md,
  },
  failedTitle: {
    margin: 0,
    fontSize: typographyFlat.fontSize.lg,
    color: colors.text,
  },
  failedList: {
    marginTop: spacing.sm,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  failedItem: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: spacing.sm,
    backgroundColor: colors.gray50,
  },
  failedName: {
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  failedMeta: {
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  failedReason: {
    marginTop: spacing.xs,
    color: colors.error,
    fontSize: typography.caption.fontSize,
  },
  actionsRow: {
    display: 'flex',
    gap: spacing.md,
    marginTop: spacing.lg,
    flexWrap: 'wrap' as const,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.primary,
    color: colors.white,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    cursor: 'pointer',
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.white,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    cursor: 'pointer',
  },
  importSummary: {
    marginTop: spacing.md,
    color: colors.success,
    fontSize: typography.small.fontSize,
  },
  errorBox: {
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.sm,
    backgroundColor: `${colors.error}12`,
    color: colors.error,
    padding: spacing.md,
    fontSize: typography.small.fontSize,
  },
  successBox: {
    border: `1px solid ${colors.success}`,
    borderRadius: borderRadius.sm,
    backgroundColor: `${colors.success}12`,
    color: colors.success,
    padding: spacing.md,
    fontSize: typography.small.fontSize,
  },
};

export default InventoryParsePage;
